import type { RequestConfig } from '@umijs/max';
import type { RequestOptions } from '@@/plugin-request/request';
import { message } from 'antd';
import { createElement } from 'react';
import {
  API_ERROR_CODE_SERVER_ERROR,
  type ApiResponse,
  isApiResponse,
  isApiSuccessCode,
  isGatewayBizSuccess,
} from '@/services/apiResponse';
import { isGatewayProxyApiUrl } from '@/services/util';
import {
  isRefreshTokenRequest,
  refreshAuthTokens,
} from '@/services/user/tokenRefresh';
import { redirectToLoginPage } from '@/utils/authSession';
import { syncCurrentUserToInitialState } from '@/utils/initialStateUser';

interface BizErrorInfo {
  message?: string;
  code?: string;
  data?: unknown;
}

type HttpError = Error & {
  response?: Record<string, unknown> & {
    status?: number;
    data?: unknown;
  };
  config?: RequestOptions;
};

const HTTP_STATUS_UNAUTHORIZED = 401;
const AUTH_RETRY_FLAG = '_authRetried';

const AUTH_REDIRECT_ERROR = 'AuthRedirectError';

const showRequestError = (content: string) => {
  const lines = content.trim().split(/\r?\n|\\n/);
  if (lines.length <= 1) {
    message.error(lines[0] ?? content);
    return;
  }
  message.error(
    createElement(
      'div',
      null,
      lines.map((line, index) =>
        createElement('div', { key: index }, line || '\u00A0'),
      ),
    ),
  );
};

const createAuthRedirectError = () => {
  const error = new Error('Unauthorized') as Error & { name: string };
  error.name = AUTH_REDIRECT_ERROR;
  return error;
};

const redirectToLogin = () => {
  redirectToLoginPage();
};

const isHttpUnauthorized = (status?: number) => status === HTTP_STATUS_UNAUTHORIZED;

const hasAuthRetried = (config?: RequestOptions) =>
  Boolean(config && (config as Record<string, unknown>)[AUTH_RETRY_FLAG]);

/** HTTP 401：刷新 token 后由拦截器重试原请求，失败则跳转登录 */
const handleHttpUnauthorized = async (
  config: RequestOptions,
): Promise<'retry' | 'abort'> => {
  if (isRefreshTokenRequest(config.url) || hasAuthRetried(config)) {
    redirectToLogin();
    return 'abort';
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    redirectToLogin();
    return 'abort';
  }

  const refreshed = await refreshAuthTokens();
  if (!refreshed) {
    redirectToLogin();
    return 'abort';
  }

  await syncCurrentUserToInitialState();

  return 'retry';
};

const toHeaderRecord = (headers?: RequestOptions['headers']): Record<string, string> => {
  if (!headers) {
    return {};
  }
  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }
  const record: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
    if (value != null) {
      record[key] = String(value);
    }
  }
  return record;
};

const buildRetryUrl = (config: RequestOptions) => {
  const url = config.url as string;
  const params = config.params as Record<string, unknown> | undefined;
  if (!params) {
    return url;
  }
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null) {
      search.append(key, String(value));
    }
  }
  const query = search.toString();
  if (!query) {
    return url;
  }
  return url.includes('?') ? `${url}&${query}` : `${url}?${query}`;
};

const buildRetryBody = (config: RequestOptions): BodyInit | undefined => {
  const method = (config.method || 'get').toUpperCase();
  if (!config.data || method === 'GET' || method === 'HEAD') {
    return undefined;
  }
  if (typeof config.data === 'string' || config.data instanceof FormData) {
    return config.data;
  }
  return JSON.stringify(config.data);
};

type RetryResponse = {
  data: unknown;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  config: RequestOptions;
};

/** 用 fetch 重试，避免直接依赖 axios（pnpm 不会提升间接依赖） */
const retryUnauthorizedRequest = async (
  config: RequestOptions,
): Promise<RetryResponse> => {
  if (!config.url) {
    throw createAuthRedirectError();
  }

  const accessToken = localStorage.getItem('accessToken');
  const retryConfig = {
    ...config,
    headers: {
      ...toHeaderRecord(config.headers),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    [AUTH_RETRY_FLAG]: true,
  } as RequestOptions;

  const response = await fetch(buildRetryUrl(retryConfig), {
    method: (retryConfig.method || 'get').toUpperCase(),
    headers: toHeaderRecord(retryConfig.headers),
    body: buildRetryBody(retryConfig),
  });

  const contentType = response.headers.get('content-type') || '';
  const rawData = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const error = new Error('Request failed') as HttpError;
    error.response = { status: response.status, data: rawData };
    error.config = retryConfig;
    throw error;
  }

  let data: unknown = rawData;
  if (isApiResponse(rawData)) {
    const body = parseApiResponse(rawData);
    const gatewayRequest = isGatewayProxyApiUrl(config.url);
    const success = gatewayRequest
      ? isGatewayBizSuccess(body)
      : isApiSuccessCode(body.code);
    if (!success) {
      throw createBizError(body);
    }
    data = body.data;
  }

  return {
    data,
    status: response.status,
    statusText: response.statusText,
    headers: Object.fromEntries(response.headers.entries()),
    config: retryConfig,
  };
};

const createBizError = (body: ApiResponse) => {
  const error: Error & { name: string; info: BizErrorInfo } = new Error(
    body.message || '请求失败',
  ) as Error & { name: string; info: BizErrorInfo };
  error.name = 'BizError';
  error.info = {
    message: body.message,
    code: body.code,
    data: body.data,
  };
  return error;
};

const parseApiResponse = (body: unknown): ApiResponse => {
  if (!isApiResponse(body)) {
    throw createBizError({
      code: API_ERROR_CODE_SERVER_ERROR,
      message: '响应格式错误',
      data: null,
    });
  }
  return body;
};

const request: RequestConfig = {
  baseURL: '',
  timeout: 30000,
  errorConfig: {
    errorThrower: (res) => {
      if (!isApiResponse(res)) {
        return;
      }
      // 网关代理成功时拦截器已解包为 data，此处仅处理仍带信封的失败体
      if (isGatewayBizSuccess(res) || isApiSuccessCode(res.code)) {
        return;
      }
      throw createBizError(res);
    },
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) {
        throw error;
      }

      if (error.name === AUTH_REDIRECT_ERROR) {
        return;
      }

      if (isHttpUnauthorized(error.response?.status)) {
        redirectToLogin();
        return;
      }

      if (error.name === 'BizError') {
        const errorInfo = error.info as BizErrorInfo | undefined;
        const bizMessage = errorInfo?.message || '请求失败';
        showRequestError(bizMessage);
        return;
      }

      const responseBody = error?.response?.data;
      if (isApiResponse(responseBody)) {
        showRequestError(responseBody.message || '请求失败');
        return;
      }

      if (error.response) {
        showRequestError('请求失败');
        return;
      }
      if (error.request) {
        showRequestError('网络无响应，请稍后重试');
        return;
      }
      showRequestError('请求异常，请稍后重试');
    },
  },

  requestInterceptors: [
    (config: RequestOptions) => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        config.headers = {
          Authorization: `Bearer ${accessToken}`,
          ...config.headers,
        };
      }

      config.headers = {
        ...config.headers,
        'Accept-Language': localStorage.getItem('umi_locale') || 'zh-CN',
      };

      if (!config.headers?.['Content-Type']) {
        config.headers = {
          ...config.headers,
          'Content-Type': 'application/json',
        };
      }

      return config;
    },
  ],

  // umi 类型定义要求错误拦截器返回 Promise<Error>，但返回 response 可完成重试
  responseInterceptors: [
    [
      (response) => {
        const requestUrl =
          typeof response.config?.url === 'string' ? response.config.url : '';
        // text/plain / OpenAI 兼容等非业务信封响应整包透传
        if (!isApiResponse(response.data)) {
          return response;
        }
        const body = parseApiResponse(response.data);
        const gatewayRequest = isGatewayProxyApiUrl(requestUrl);
        const success = gatewayRequest
          ? isGatewayBizSuccess(body)
          : isApiSuccessCode(body.code);

        if (!success) {
          throw createBizError(body);
        }

        return {
          ...response,
          data: body.data,
        } as typeof response;
      },
      async (error: HttpError) => {
        if (!isHttpUnauthorized(error.response?.status)) {
          return Promise.reject(error);
        }

        if (!error.config) {
          redirectToLogin();
          return Promise.reject(createAuthRedirectError());
        }

        const action = await handleHttpUnauthorized(error.config);
        if (action === 'abort') {
          return Promise.reject(createAuthRedirectError());
        }

        try {
          return await retryUnauthorizedRequest(error.config);
        } catch (retryError) {
          return Promise.reject(retryError);
        }
      },
    ],
  ] as NonNullable<RequestConfig['responseInterceptors']>,
};

export default request;
